import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Group, Modal, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useResponsive } from "../../../context/ResponsiveContext";
import {
  isDuplicateTagNameError,
  useAppointmentTags,
} from "../../../hooks/useAppointmentTags";
import {
  AppointmentTag,
  MAX_APPOINTMENT_TAG_NAME_LENGTH,
} from "../../../types/appointmentTags";
import { notifySuccess } from "../../../utils/notify";
import TagEmojiPicker from "../../appointmentTags/TagEmojiPicker";

export interface AppointmentTagFormModalProps {
  opened: boolean;
  onClose: () => void;
  /** The tag being edited, or null to create a new one. */
  tag: AppointmentTag | null;
  /** Called after a successful save so the list can refetch. */
  onSaved: () => void;
}

interface TagFormValues {
  name: string;
  emoji: string | null;
}

const EMPTY_VALUES: TagFormValues = { name: "", emoji: null };

/** Mirrors the backend: trimmed, required, ≤ 50 characters. */
export function validateTagName(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return "Name is required";
  if (trimmed.length > MAX_APPOINTMENT_TAG_NAME_LENGTH) {
    return `Name must be ${MAX_APPOINTMENT_TAG_NAME_LENGTH} characters or less`;
  }
  return null;
}

/**
 * Add / edit an appointment tag: a name field with the emoji picker inside it
 * on the left (default = tag icon), per the design. A duplicate name (backend
 * 409, case-insensitive) is shown on the Name field; any other failure shows
 * an inline alert.
 */
export default function AppointmentTagFormModal({
  opened,
  onClose,
  tag,
  onSaved,
}: AppointmentTagFormModalProps) {
  const { createTag, updateTag } = useAppointmentTags();
  const { isMobile } = useResponsive();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<TagFormValues>({
    initialValues: EMPTY_VALUES,
    validate: { name: validateTagName },
  });

  // `form` is re-created each render; mirror it so the open effect only
  // re-runs when the modal opens or the target tag changes.
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    if (!opened) return;
    setSubmitError(null);
    formRef.current.setValues(
      tag ? { name: tag.name, emoji: tag.emoji ?? null } : EMPTY_VALUES,
    );
    formRef.current.clearErrors();
    formRef.current.resetDirty();
  }, [opened, tag]);

  const handleSubmit = async (values: TagFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    const body = { name: values.name.trim(), emoji: values.emoji };
    try {
      if (tag) {
        await updateTag(tag.id, body);
        notifySuccess(`Saved ${body.name}`, { title: "Tag updated" });
      } else {
        await createTag(body);
        notifySuccess(`Added ${body.name}`, { title: "Tag added" });
      }
      onSaved();
      onClose();
    } catch (error) {
      if (isDuplicateTagNameError(error)) {
        form.setFieldError(
          "name",
          `A tag named "${body.name}" already exists.`,
        );
      } else {
        setSubmitError(
          (error as Error | null)?.message ||
            "Failed to save tag. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={tag ? "Edit Tag" : "Add Tag"}
      size="md"
      fullScreen={isMobile}
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
        <Stack gap="md">
          {submitError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="var(--color-danger)"
            >
              {submitError}
            </Alert>
          )}

          <TextInput
            label="Name"
            description="Pick an icon on the left, or keep the default tag icon."
            placeholder="e.g. VIP"
            required
            maxLength={MAX_APPOINTMENT_TAG_NAME_LENGTH}
            data-autofocus
            leftSection={
              <TagEmojiPicker
                value={form.values.emoji}
                onChange={(emoji) => form.setFieldValue("emoji", emoji)}
                disabled={isSubmitting}
              />
            }
            leftSectionWidth={40}
            leftSectionPointerEvents="all"
            {...form.getInputProps("name")}
          />

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="var(--brand-teal)"
              loading={isSubmitting}
            >
              {tag ? "Save Changes" : "Add Tag"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
